import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { MOCK_PLANTS, MOCK_DEPARTMENTS, MOCK_COST_CENTERS } from '@/mocks/database';
import { ProcurementCategory, PriorityLevel, ExpenseType } from '@/types/domain';
import {
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  SlidersHorizontal,
  Flame,
  Package,
  Briefcase,
  Layers,
} from 'lucide-react';

interface RequestHeaderSectionProps {
  title: string;
  setTitle: (v: string) => void;
  category: ProcurementCategory;
  setCategory: (v: ProcurementCategory) => void;
  procurementType: 'Material' | 'Service';
  setProcurementType: (v: 'Material' | 'Service') => void;
  expenseType: ExpenseType;
  setExpenseType: (v: ExpenseType) => void;
  priority: PriorityLevel;
  setPriority: (v: PriorityLevel) => void;
  requiredDate: string;
  setRequiredDate: (v: string) => void;
  companyCode: string;
  setCompanyCode: (v: string) => void;
  plantCode: string;
  setPlantCode: (v: string) => void;
  departmentCode: string;
  setDepartmentCode: (v: string) => void;
  costCenterCode: string;
  setCostCenterCode: (v: string) => void;
  requesterName?: string;
  employeeId?: string;
  errors?: Record<string, string>;
}

export const RequestHeaderSection: React.FC<RequestHeaderSectionProps> = ({
  title,
  setTitle,
  category,
  setCategory,
  procurementType,
  setProcurementType,
  expenseType: _expenseType,
  setExpenseType,
  priority,
  setPriority,
  requiredDate,
  setRequiredDate,
  companyCode: _companyCode,
  setCompanyCode: _setCompanyCode,
  plantCode,
  setPlantCode,
  departmentCode,
  setDepartmentCode,
  costCenterCode,
  setCostCenterCode,
  requesterName: _requesterName = 'Sarah Jenkins',
  employeeId: _employeeId = 'EMP-88402',
  errors = {},
}) => {
  const [showAdvancedAccounting, setShowAdvancedAccounting] = useState(false);

  // Auto-infer expense type from category changes
  const handleCategoryChange = (newCategory: ProcurementCategory) => {
    setCategory(newCategory);
    if (newCategory === 'CapitalEquipment') {
      setExpenseType('CAPEX');
    } else {
      setExpenseType('OPEX');
    }
  };

  const isUrgent = priority === 'Critical';
  const handleToggleUrgent = () => {
    setPriority(isUrgent ? 'Medium' : 'Critical');
  };

  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const todayDateString = new Date().toISOString().split('T')[0] || '';

  const selectedPlant = MOCK_PLANTS.find((p) => p.code === plantCode);
  const selectedDept = MOCK_DEPARTMENTS.find((d) => d.code === departmentCode);

  return (
    <Card className="border border-border/80 shadow-xs rounded-2xl overflow-hidden font-poppins">
      <CardHeader className="pb-3.5 border-b border-border/60 bg-muted/20">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-bold flex items-center gap-2.5 text-foreground">
            <div className="h-8 w-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Building2 className="h-4 w-4" />
            </div>
            <div>
              <span>Requisition Details</span>
              <p className="text-xs font-medium text-foreground-secondary mt-0.5">
                Primary procurement classification and scheduling
              </p>
            </div>
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="pt-5 pb-6 px-6 space-y-5">
        {/* Procurement Classification Switcher (Goods vs Services) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-muted/30 border border-border/80 rounded-2xl">
          <div className="space-y-0.5">
            <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5 text-primary" /> Procurement Classification <span className="text-destructive">*</span>
            </span>
            <p className="text-[11px] text-foreground-secondary">
              Select whether this requisition is for physical items (Goods & Materials) or professional scope (Services & SOW).
            </p>
          </div>
          <div className="flex items-center gap-1.5 p-1 bg-surface dark:bg-card border border-border rounded-xl shrink-0">
            <button
              type="button"
              onClick={() => setProcurementType('Material')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
                procurementType === 'Material'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-foreground-secondary hover:text-foreground'
              }`}
            >
              <Package className="h-3.5 w-3.5" />
              <span>Goods & Materials</span>
            </button>
            <button
              type="button"
              onClick={() => setProcurementType('Service')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
                procurementType === 'Service'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-foreground-secondary hover:text-foreground'
              }`}
            >
              <Briefcase className="h-3.5 w-3.5" />
              <span>Services & SOW</span>
            </button>
          </div>
        </div>

        {/* Core Field 1: Requisition Title */}
        <div className="space-y-1.5">
          <label className="text-sm font-bold text-foreground flex items-center justify-between h-6">
            <span className="flex items-center gap-1">
              Requisition Title <span className="text-destructive">*</span>
            </span>
            <span className="text-xs font-normal text-foreground-muted">
              Clear description of required goods or services
            </span>
          </label>
          <input
            type="text"
            className={`w-full h-11 px-3.5 text-sm rounded-xl border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all font-medium ${
              errors.title ? 'border-destructive focus:ring-destructive/30' : 'border-border'
            }`}
            placeholder="e.g. Server Hardware Expansion for Analytics Cluster"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          {errors.title && (
            <p className="text-xs font-semibold text-destructive flex items-center gap-1 mt-1">
              <AlertCircle className="h-3.5 w-3.5" /> {errors.title}
            </p>
          )}
        </div>

        {/* Core Fields: Perfectly Aligned 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
          {/* Category Picker */}
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-foreground flex items-center gap-1 h-6">
              <span>Category</span> <span className="text-destructive">*</span>
            </label>
            <select
              className="w-full h-11 px-3.5 text-xs font-bold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all cursor-pointer"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value as ProcurementCategory)}
            >
              <option value="RawMaterial">Raw Material</option>
              <option value="CapitalEquipment">Capital Equipment (CAPEX)</option>
              <option value="ITServices">IT Services & Hardware</option>
              <option value="MRO">MRO (Maintenance & Operations)</option>
              <option value="Logistics">Logistics & Supply Chain</option>
              <option value="ProfessionalServices">Professional Services</option>
            </select>
          </div>

          {/* Required Delivery Date - Block Past Dates & Click Anywhere to Open */}
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-foreground flex items-center gap-1.5 h-6">
              <Calendar className="h-4 w-4 text-primary" />
              <span>Required Delivery Date</span> <span className="text-destructive">*</span>
            </label>
            <div
              onClick={() => {
                dateInputRef.current?.showPicker?.();
                dateInputRef.current?.focus();
              }}
              className={`relative w-full h-11 px-3.5 rounded-xl border bg-surface flex items-center cursor-pointer hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/30 transition-all ${
                errors.requiredDate ? 'border-destructive' : 'border-border'
              }`}
            >
              <input
                ref={dateInputRef}
                type="date"
                min={todayDateString}
                onClick={(e) => {
                  e.stopPropagation();
                  dateInputRef.current?.showPicker?.();
                }}
                className="w-full h-full bg-transparent text-xs font-bold font-mono text-foreground focus:outline-none cursor-pointer"
                value={requiredDate}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && val < todayDateString) {
                    setRequiredDate(todayDateString);
                  } else {
                    setRequiredDate(val);
                  }
                }}
              />
            </div>
            {errors.requiredDate && (
              <p className="text-xs font-semibold text-destructive mt-1">{errors.requiredDate}</p>
            )}
          </div>

          {/* Urgent / Priority Toggle Button */}
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-foreground flex items-center gap-1 h-6">
              <span>Fulfillment Priority</span>
            </label>
            <button
              type="button"
              onClick={handleToggleUrgent}
              className={`w-full h-11 px-3.5 rounded-xl border text-xs font-extrabold flex items-center justify-between transition-all cursor-pointer ${
                isUrgent
                  ? 'bg-rose-500/15 border-rose-500/30 text-rose-700 dark:text-rose-400 shadow-xs'
                  : 'bg-surface border-border text-foreground-secondary hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Flame className={`h-4 w-4 ${isUrgent ? 'text-rose-500 fill-rose-500' : 'text-foreground-muted'}`} />
                {isUrgent ? 'Marked as Critical / Urgent' : 'Standard Priority (Medium)'}
              </span>
              <Badge variant={isUrgent ? 'destructive' : 'outline'} className="text-[10px] font-black">
                {isUrgent ? 'HIGH PRIORITY' : 'DEFAULT'}
              </Badge>
            </button>
          </div>
        </div>

        {/* Collapsible Accounting & Cost Allocation Context */}
        <div className="pt-2 border-t border-border/60">
          <button
            type="button"
            onClick={() => setShowAdvancedAccounting(!showAdvancedAccounting)}
            className="flex items-center justify-between w-full py-2 text-xs font-bold text-foreground-secondary hover:text-foreground transition-colors cursor-pointer group"
          >
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-3.5 w-3.5 text-primary" />
              <span>Accounting & Location Allocations</span>
              <span className="text-[11px] font-normal text-foreground-muted">
                (Defaulted to {selectedDept?.name} • {selectedPlant?.name} • Cost Center {costCenterCode})
              </span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-primary group-hover:underline">
              <span>{showAdvancedAccounting ? 'Hide Allocations' : 'Change Cost Center / Plant'}</span>
              {showAdvancedAccounting ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </div>
          </button>

          {showAdvancedAccounting && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 mt-2 rounded-2xl bg-muted/40 border border-border/80 animate-in fade-in zoom-in-95 duration-150">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Delivery Plant</label>
                <select
                  className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={plantCode}
                  onChange={(e) => setPlantCode(e.target.value)}
                >
                  {MOCK_PLANTS.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.code} - {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Charging Department</label>
                <select
                  className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={departmentCode}
                  onChange={(e) => setDepartmentCode(e.target.value)}
                >
                  {MOCK_DEPARTMENTS.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.code} - {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Cost Center Allocation</label>
                <select
                  className="w-full px-3 py-2 text-xs font-mono font-semibold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={costCenterCode}
                  onChange={(e) => setCostCenterCode(e.target.value)}
                >
                  {MOCK_COST_CENTERS.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.code} ({cc.departmentCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
